/**
 * `field_key` — the stable output key of a binding (CT-5641).
 *
 * Every field binding carries one, and it is the key the shaped response is
 * read under: `/data/rows/{i}/values/{field_key}`, `/data/series/{i}/points`,
 * and so on through the 29 `x-output-path`s. It is also the alias the sync gives
 * the generated WIDGET field, which is why it has to satisfy the engine's
 * alias grammar and be unique inside one widget.
 *
 * ## Why it is derived rather than typed
 *
 * The author never writes one. They pick a column and, on a measure, an
 * operation; the key follows from those two deterministically, so the same
 * selection always produces the same definition and a re-save of an untouched
 * widget rewrites nothing. Guide 18 §3.4 says the same from the other side: a
 * new key must not be minted when only `label` or `format` changed.
 *
 * ## Why the operation is in the name
 *
 * `field` and `field_key` are allowed to be equal when nothing is done to the
 * column, and they must not be when something is: `SUM(total_amount)` is not
 * `total_amount`, and binding both to a widget — a table column beside a
 * summed measure — would otherwise collide on one key and silently drop one
 * of them. So an operation suffixes the key with its own name:
 * `total_amount` + `SUM` → `total_amount_sum`, `created_at` + `MONTH` →
 * `created_at_month`.
 *
 * `COUNT_ROWS` has no column to name, so it keys on the operation alone.
 */

import {
  AGGREGATE_WITHOUT_FIELD,
  FIELD_KEY_MAX_LENGTH,
  FIELD_KEY_PATTERN,
} from '../constants/widgetContract.js';

/** What `COUNT_ROWS` is called when it has no column to take a name from. */
export const ROW_COUNT_FIELD_KEY = 'record_count';

/**
 * Fold any string into the alias grammar, or `''` when nothing survives.
 *
 * Anything outside `a-z0-9_` becomes `_`, uppercase is folded, a value that
 * does not start with a letter is prefixed rather than rejected (a field code
 * may legally start with a digit; an alias may not), and the result is cut to
 * length.
 */
export function shapeFieldKey(value) {
  const folded = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^_+/, '');
  if (!folded) return '';
  const headed = /^[a-z]/.test(folded) ? folded : `f_${folded}`;
  return headed.slice(0, FIELD_KEY_MAX_LENGTH);
}

/** Does this value satisfy the engine's alias grammar as it stands? */
export const isFieldKey = (value) => FIELD_KEY_PATTERN.test(String(value ?? ''));

/**
 * The first spelling of `base` that nothing has claimed.
 *
 * `_2` onwards, in the order keys are minted, so the first binding to want a
 * name keeps it and the rest are deterministic rather than ordered by luck.
 *
 * @param {string} base A key already through `shapeFieldKey`.
 * @param {Set<string>|string[]} taken Keys already spoken for.
 */
export function uniqueFieldKey(base, taken = new Set()) {
  const claimed = taken instanceof Set ? taken : new Set(taken);
  let key = base;
  for (let n = 2; claimed.has(key); n += 1) {
    const suffix = `_${n}`;
    key = `${base.slice(0, FIELD_KEY_MAX_LENGTH - suffix.length)}${suffix}`;
  }
  return key;
}

/**
 * The key a binding should carry, from the column and the operation on it.
 *
 * @param {object} params
 * @param {string} [params.field] The DATASET column's alias. Absent for
 *   `COUNT_ROWS`, which counts rows rather than reading a column.
 * @param {string} [params.aggregate] The measure's operation.
 * @param {string} [params.timeGrain] The bucket a date dimension groups into.
 * @param {string} [params.datePart] The part a date dimension extracts.
 * @param {Set<string>|string[]} [params.taken] Keys already used by this
 *   widget's other bindings.
 * @returns {string} A key that satisfies the grammar and is free, or `''` when
 *   there is nothing to name it after — a caller with neither a column nor an
 *   operation has not finished the binding, and an invented key would read as
 *   if they had.
 */
export function deriveFieldKey({
  field = '',
  aggregate = '',
  timeGrain = '',
  datePart = '',
  taken = new Set(),
} = {}) {
  const column = shapeFieldKey(field);
  const operation = shapeFieldKey(aggregate || timeGrain || datePart);

  if (aggregate === AGGREGATE_WITHOUT_FIELD) return uniqueFieldKey(ROW_COUNT_FIELD_KEY, taken);
  if (!column) return '';
  // An untouched column keys on itself: `field` and `field_key` are allowed to
  // be the same string, and a suffix nobody asked for would only be noise.
  if (!operation) return uniqueFieldKey(column, taken);

  const suffix = `_${operation}`;
  const base = `${column.slice(0, FIELD_KEY_MAX_LENGTH - suffix.length)}${suffix}`;
  return uniqueFieldKey(base, taken);
}

export default deriveFieldKey;
